import React from "react";
import { Badge } from "components/ui/badge";
import { SectionCard, DefinitionList, FlagPill } from "./components";

// Nice labels for the loadFactorV2.inputs keys, in render order.
const INPUT_LABELS = {
    video: "Video",
    audio: "Audio",
    profiles: "Profiles",
    outlets: "Outlets",
    mta: "MTA",
    dvr: "DVR",
    hls_seg_dur: "HLS seg",
};

// Renders the grouped "Formatted" view of a live event's schedule.
function ScheduleFormatted({ formatted }) {
    if (!formatted || Object.keys(formatted).length === 0) {
        return <p className="mt-4 text-sm text-muted-foreground">No event schedule to display.</p>;
    }

    const { overview, timeline, specification, variants, ingestFeeds, loadFactor } = formatted;

    return (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {overview && <OverviewCard overview={overview} />}
            {timeline && <TimelineCard timeline={timeline} />}
            {specification && <SpecificationCard specification={specification} />}
            {variants && <VariantsCard variants={variants} />}
            {ingestFeeds && <IngestFeedsCard ingestFeeds={ingestFeeds} />}
            {loadFactor && <LoadFactorCard loadFactor={loadFactor} />}
        </div>
    );
}

function OverviewCard({ overview }) {
    return (
        <SectionCard title="Overview">
            <DefinitionList
                items={[
                    { label: "Description", value: overview.description },
                    { label: "Channel", value: overview.channel, mono: true },
                    {
                        label: "Tier",
                        value: overview.tier != null ? <Badge variant="outline">{overview.tier}</Badge> : null,
                    },
                    { label: "Version", value: overview.version, mono: true },
                    {
                        label: "Event type",
                        value: overview.eventType ? <Badge variant="secondary">{overview.eventType}</Badge> : null,
                    },
                    {
                        label: "Event URL",
                        value: overview.eventUrl ? (
                            <a
                                href={overview.eventUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="break-all text-primary hover:underline"
                            >
                                Open event ↗
                            </a>
                        ) : null,
                    },
                ]}
            />
        </SectionCard>
    );
}

function TimelineCard({ timeline }) {
    return (
        <SectionCard title="Timeline">
            <DefinitionList
                items={[
                    { label: "Event day", value: timeline.day, mono: true },
                    { label: "Stream start", value: timeline.streamStart, mono: true },
                    { label: "Stream end", value: timeline.streamEnd, mono: true },
                    { label: "Duration", value: timeline.duration },
                ]}
            />
        </SectionCard>
    );
}

function SpecificationCard({ specification }) {
    return (
        <SectionCard title="Specification" className="lg:col-span-2">
            {specification.flags?.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                    {specification.flags.map((flag) => (
                        <FlagPill key={flag.label} label={flag.label} value={flag.value} />
                    ))}
                </div>
            )}
            <div className="grid grid-cols-1 gap-x-6 gap-y-1.5 md:grid-cols-2">
                <DefinitionList
                    items={[
                        {
                            label: "Policies",
                            value: <BadgeList items={specification.policies} variant="secondary" />,
                        },
                        {
                            label: "Watermarking",
                            value: specification.watermarking ? (
                                <Badge variant="outline">{specification.watermarking}</Badge>
                            ) : null,
                        },
                        {
                            label: "Motion graphics",
                            value: specification.motionGraphics ? (
                                <Badge variant="outline">{specification.motionGraphics}</Badge>
                            ) : null,
                        },
                        {
                            label: "HE resilience",
                            value: specification.heResilience ? (
                                <Badge variant="outline">{specification.heResilience}</Badge>
                            ) : null,
                        },
                    ]}
                />
                <DefinitionList
                    items={[
                        { label: "Competition ID", value: specification.competitionId, mono: true },
                        { label: "Rights ID", value: specification.rightsId, mono: true },
                        { label: "Client content ID", value: specification.clientContentId, mono: true },
                        { label: "Regional supplier", value: specification.regionalSupplierId, mono: true },
                    ]}
                />
            </div>
        </SectionCard>
    );
}

function VariantsCard({ variants }) {
    const outletCount = variants.outlets?.length || 0;
    const summary = [
        `${outletCount} outlet${outletCount === 1 ? "" : "s"}`,
        variants.mapping ? `mapping: ${variants.mapping}` : null,
    ]
        .filter(Boolean)
        .join(" · ");

    return (
        <SectionCard title="Variants" count={summary} className="lg:col-span-2">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {variants.outlets.map((outlet) => (
                    <div key={outlet.name} className="rounded-lg border border-border bg-muted/30 p-3">
                        <div className="mb-2 flex items-center gap-2">
                            <Badge variant="default">{outlet.name}</Badge>
                        </div>
                        <DefinitionList
                            items={[
                                {
                                    label: "Languages",
                                    value: <BadgeList items={outlet.languages} variant="secondary" />,
                                },
                                { label: "Stream ID", value: outlet.streamId, mono: true },
                                { label: "Outlet API ID", value: outlet.outletApiId, mono: true },
                                {
                                    label: "Closed captions",
                                    value:
                                        outlet.closedCaptions == null ? null : outlet.closedCaptions.length ? (
                                            <BadgeList items={outlet.closedCaptions} variant="secondary" />
                                        ) : (
                                            <span className="text-muted-foreground">None</span>
                                        ),
                                },
                            ]}
                        />
                    </div>
                ))}
            </div>
        </SectionCard>
    );
}

function IngestFeedsCard({ ingestFeeds }) {
    return (
        <SectionCard title="Ingest &amp; Feeds" className="lg:col-span-2">
            <div className="mb-3">
                <DefinitionList
                    items={[
                        {
                            label: "Source",
                            value: ingestFeeds.source ? (
                                <Badge variant="outline">{ingestFeeds.source}</Badge>
                            ) : null,
                        },
                        { label: "Perform channel", value: ingestFeeds.performChannel, mono: true },
                    ]}
                />
            </div>
            {ingestFeeds.dirtyFeeds?.length > 0 && (
                <>
                    <div className="mb-1.5 mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Dirty feeds
                    </div>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        {ingestFeeds.dirtyFeeds.map((feed) => (
                            <div
                                key={feed.name}
                                className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 p-3"
                            >
                                <span className="text-sm font-semibold">{feed.name}</span>
                                {feed.type && <Badge variant="outline">{feed.type}</Badge>}
                                <FlagPill label={feed.enabled ? "Enabled" : "Disabled"} value={feed.enabled} />
                            </div>
                        ))}
                    </div>
                </>
            )}
        </SectionCard>
    );
}

function LoadFactorCard({ loadFactor }) {
    return (
        <SectionCard
            title="Load Factor"
            count={loadFactor.lookupTable ? `v2 · lookup: ${loadFactor.lookupTable}` : "v2"}
            className="lg:col-span-2"
        >
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div>
                    <div className="flex items-baseline gap-2.5">
                        <span className="text-3xl font-bold tabular-nums">{loadFactor.loadFactor}</span>
                        <span className="text-sm text-muted-foreground">load factor</span>
                    </div>
                    <div className="mt-2.5">
                        <DefinitionList
                            items={[
                                { label: "ASG", value: loadFactor.asg, mono: true },
                                { label: "Origins / event", value: loadFactor.originsPerEvent },
                                { label: "Lookup table", value: loadFactor.lookupTable, mono: true },
                            ]}
                        />
                    </div>
                </div>
                {loadFactor.inputs?.length > 0 && (
                    <div>
                        <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Inputs
                        </div>
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(84px,1fr))] gap-2">
                            {loadFactor.inputs.map((input) => (
                                <div key={input.key} className="rounded-lg border border-border bg-card p-2">
                                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                                        {INPUT_LABELS[input.key] || input.key}
                                    </div>
                                    <div className="text-[17px] font-semibold tabular-nums">{input.value}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </SectionCard>
    );
}

// ---- small local helpers ----

function BadgeList({ items, variant }) {
    if (!items || items.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-1.5">
            {items.map((item) => (
                <Badge key={item} variant={variant}>
                    {item}
                </Badge>
            ))}
        </div>
    );
}

export default ScheduleFormatted;
